import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ArrowUpRight } from "lucide-react";
import { EASE } from "./Reveal";
import { ResumeCard } from "./ResumeCard";
import { mailto } from "./content";
import { scrollToId } from "./SmoothScroll";
import { Link } from "react-router-dom";

const LINES = [
  { t: "Your work,", c: "text-white" },
  { t: "rewritten to", c: "text-white" },
  { t: "prove it.", c: "italic text-[var(--teal)]" },
];

const MaskLine = ({ children, i, className }) => (
  <span className="block overflow-hidden pb-[0.08em]">
    <motion.span className={`block ${className}`} initial={{ y: "110%", rotate: 3 }} animate={{ y: "0%", rotate: 0 }}
      transition={{ duration: 1.25, ease: EASE, delay: 0.35 + i * 0.14 }}>
      {children}
    </motion.span>
  </span>
);

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const titleY = useTransform(scrollYProgress, [0, 1], [0, -120]);
  const glowX = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);

  return (
    <section id="top" ref={ref} className="relative isolate overflow-hidden pt-36 sm:pt-40" data-testid="hero-section">
      <div className="grid-lines absolute inset-0 -z-10 [mask-image:radial-gradient(ellipse_at_30%_30%,black,transparent_70%)]" />
      <motion.div style={{ x: glowX }} className="absolute -left-40 top-10 -z-10 h-[520px] w-[520px] rounded-full bg-[var(--teal-deep)]/25 blur-[140px]" />
      <div className="mx-auto grid max-w-7xl gap-14 px-5 pb-24 sm:px-8 lg:grid-cols-12 lg:gap-8 lg:pb-36">
        <motion.div style={{ y: titleY }} className="lg:col-span-7">
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2, duration: 1 }}
            className="font-mono-d mb-8 flex items-center gap-3 text-xs uppercase tracking-[0.22em] text-[var(--teal)]">
            <span className="h-px w-10 bg-[var(--teal)]" /> Resume bullet rewriting
          </motion.p>
          <h1 className="font-serif-d text-[3.4rem] leading-[0.92] tracking-[-0.02em] sm:text-7xl lg:text-[7.4rem]" data-testid="hero-headline">
            {LINES.map((l, i) => <MaskLine key={l.t} i={i} className={l.c}>{l.t}</MaskLine>)}
          </h1>
          <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.95, duration: 1, ease: EASE }}
            className="mt-9 max-w-xl text-base leading-relaxed text-slate-300 sm:text-lg">
            Send your resume and the job you want. We turn vague duties into sharp action-verb bullets with real,
            verified numbers and the keywords your target jobs look for. Delivered as Word and PDF.
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.1, duration: 1, ease: EASE }}
            className="mt-10 flex flex-wrap items-center gap-4">
            <a href={mailto("Resume bullet rewrite")} data-testid="hero-email-button"
              className="group flex items-center gap-2 rounded-full bg-[var(--teal)] px-7 py-4 text-base font-semibold text-[var(--ink)] shadow-[0_0_40px_rgba(45,226,208,0.25)] transition-transform duration-300 hover:scale-[1.03] active:scale-95">
              Send your resume <ArrowUpRight className="h-5 w-5 transition-transform duration-300 group-hover:rotate-45" />
            </a>
            <button onClick={() => scrollToId("#sample")} data-testid="hero-sample-button"
              className="group flex items-center gap-2 rounded-full border border-white/15 px-7 py-4 text-base text-white transition-colors duration-300 hover:border-[var(--teal)] hover:text-[var(--teal)]">
              See a before & after <ArrowDown className="h-4 w-4 transition-transform duration-300 group-hover:translate-y-1" />
            </button>
          </motion.div>
          <p className="font-mono-d mt-8 text-xs text-slate-400">From $75 · Word + PDF · No invented numbers</p>
          <Link to="/bullet-checker" data-testid="hero-checker-link" className="group mt-3 inline-flex items-center gap-1.5 text-sm text-[var(--teal)] hover:underline">
            Not sure yet? Score one of your bullets free <ArrowUpRight className="h-4 w-4 transition-transform group-hover:rotate-45" />
          </Link>
        </motion.div>
        <div className="lg:col-span-5 lg:pt-6">
          <ResumeCard />
        </div>
      </div>
    </section>
  );
};
